import { create } from 'zustand';

interface AssetSelectionState {
  /** All selected IDs (both assets and folders) */
  selectedIds: Set<string>;
  /** Subset of selectedIds that are folder/group IDs */
  selectedGroupIds: Set<string>;
  lastClickedId: string | null;
  toggleSelected: (id: string, isGroup?: boolean) => void;
  selectRange: (fromId: string, toId: string, allIds: string[]) => void;
  setLastClicked: (id: string) => void;
  selectAll: (assetIds: string[], groupIds?: string[]) => void;
  /** Drop specific ids from the selection (e.g. rows that left the Favorites view). */
  deselect: (ids: string[]) => void;
  clearSelection: () => void;
  isSelected: (id: string) => boolean;
  selectedCount: () => number;
  /** Returns only asset IDs (selectedIds minus selectedGroupIds) */
  getAssetIds: () => string[];
  /** Returns only group/folder IDs */
  getGroupIds: () => string[];
}

export const useAssetSelectionStore = create<AssetSelectionState>((set, get) => ({
  selectedIds: new Set(),
  selectedGroupIds: new Set(),
  lastClickedId: null,

  toggleSelected: (id, isGroup = false) =>
    set((state) => {
      const next = new Set(state.selectedIds);
      const nextGroupIds = new Set(state.selectedGroupIds);
      if (next.has(id)) {
        next.delete(id);
        nextGroupIds.delete(id);
      } else {
        next.add(id);
        if (isGroup) {
          nextGroupIds.add(id);
        }
      }
      return { selectedIds: next, selectedGroupIds: nextGroupIds, lastClickedId: id };
    }),

  setLastClicked: (id) => set({ lastClickedId: id }),

  selectRange: (fromId, toId, allIds) =>
    set((state) => {
      const fromIndex = allIds.indexOf(fromId);
      const toIndex = allIds.indexOf(toId);
      if (fromIndex === -1 || toIndex === -1) return state;

      const start = Math.min(fromIndex, toIndex);
      const end = Math.max(fromIndex, toIndex);
      const rangeIds = allIds.slice(start, end + 1);

      const next = new Set(state.selectedIds);
      for (const id of rangeIds) {
        next.add(id);
      }
      return { selectedIds: next, lastClickedId: toId };
    }),

  selectAll: (assetIds, groupIds = []) =>
    set({
      selectedIds: new Set([...assetIds, ...groupIds]),
      selectedGroupIds: new Set(groupIds),
    }),
  deselect: (ids) =>
    set((state) => {
      if (ids.length === 0) return state;
      const next = new Set(state.selectedIds);
      const nextGroupIds = new Set(state.selectedGroupIds);
      let changed = false;
      for (const id of ids) {
        if (next.delete(id)) changed = true;
        nextGroupIds.delete(id);
      }
      if (!changed) return state;
      return {
        selectedIds: next,
        selectedGroupIds: nextGroupIds,
        lastClickedId:
          state.lastClickedId && ids.includes(state.lastClickedId) ? null : state.lastClickedId,
      };
    }),
  clearSelection: () =>
    set({ selectedIds: new Set(), selectedGroupIds: new Set(), lastClickedId: null }),
  isSelected: (id) => get().selectedIds.has(id),
  selectedCount: () => get().selectedIds.size,
  getAssetIds: () => {
    const { selectedIds, selectedGroupIds } = get();
    return Array.from(selectedIds).filter((id) => !selectedGroupIds.has(id));
  },
  getGroupIds: () => Array.from(get().selectedGroupIds),
}));
